import React, { useState } from "react";
import { createConsultation } from "../lib/api";
import { toast } from "sonner";
import { Calendar, ArrowUpRight, Loader2 } from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  topic: "",
  preferred_time: "",
  message: "",
};

export const Consult = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.topic.trim() || !form.message.trim()) {
      toast.error("Name, email, topic and message are required.");
      return;
    }
    setLoading(true);
    try {
      await createConsultation(form);
      toast.success("Consultation request received. We'll confirm within 24h.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 pb-10" data-testid="consult-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
            Consult
          </p>
          <h1 className="font-display text-5xl md:text-7xl font-black text-white mt-4 leading-[0.9] tracking-tighter">
            Book a <span className="text-gradient-neon">strategy</span> call.
          </h1>
          <p className="text-zinc-400 mt-6 text-base">
            45 minutes. We map your top 3 automation opportunities and show
            you how we'd build them — for free.
          </p>

          <div className="mt-10 space-y-4">
            {[
              "Workflow audit + ROI estimate",
              "Architecture sketch on the call",
              "Honest 'don't automate this' takes",
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-3 text-zinc-200 text-sm"
              >
                <Calendar
                  className="w-5 h-5 text-[#00F0FF]"
                  strokeWidth={1.6}
                />
                {t}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="glass-strong rounded-3xl p-8 md:p-10 space-y-5"
            data-testid="consult-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">
                  Name
                </label>
                <input
                  data-testid="consult-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">
                  Email
                </label>
                <input
                  data-testid="consult-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">
                  Phone (optional)
                </label>
                <input
                  data-testid="consult-phone"
                  type="text"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">
                  Company (optional)
                </label>
                <input
                  data-testid="consult-company"
                  type="text"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">
                Topic
              </label>
              <input
                data-testid="consult-topic"
                type="text"
                value={form.topic}
                onChange={(e) => update("topic", e.target.value)}
                placeholder="e.g. AI lead routing"
                className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">
                Preferred time
              </label>
              <input
                data-testid="consult-time"
                type="text"
                value={form.preferred_time}
                onChange={(e) => update("preferred_time", e.target.value)}
                placeholder="e.g. Weekdays 10–4 PT"
                className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">
                What would make this call a 10/10?
              </label>
              <textarea
                data-testid="consult-message"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="consult-submit"
              className="w-full bg-[#00F0FF] text-black font-bold py-4 rounded-xl hover:bg-[#00C8E0] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Book my call <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Consult;
